import { Box, Link, Typography } from '@mui/material';
import type { NewsEdit, NewsEditSource } from '../store/slices/admin-slice';

interface NewsItemPreviewProps {
  readonly values: NewsEdit;
  /** Pre-filtered sources (blank rows removed) so the preview line is clean. */
  readonly sources: ReadonlyArray<NewsEditSource>;
}

/**
 * `<NewsItemPreview>` — live preview of how a `<NewsItem>` will
 * render with the in-progress edit values. Mirrors the Daily-page's
 * `<NewsItem>` layout (heading + floated image + body + sources)
 * so the admin sees an accurate picture before saving.
 *
 * The preview deliberately omits any admin-only chrome (status
 * chip, edit badges) — it's a faithful render of what the public
 * visitor will see, full stop. The status pill lives on the
 * `<AdminEditBadge>` outside the modal so the admin can read it
 * from the Daily page directly.
 */
export function NewsItemPreview({ values, sources }: NewsItemPreviewProps): JSX.Element {
  const { title, body, image } = values;
  return (
    <Box sx={{ overflow: 'hidden' }}>
      {title && (
        <Typography
          variant="h4"
          component="h3"
          sx={{ fontSize: { xs: '1rem', sm: '1.125rem' }, fontWeight: 600, mb: 1.5 }}
        >
          {title}
        </Typography>
      )}
      {image && (
        <Box
          component="img"
          src={image}
          alt={title || 'News image'}
          sx={{
            float: 'left',
            width: 'auto',
            height: 'auto',
            maxWidth: '50%',
            maxHeight: 200,
            objectFit: 'contain',
            borderRadius: 1,
            mr: 2,
            mb: 1,
          }}
        />
      )}
      {body && <Typography variant="body1">{body}</Typography>}
      {sources.length > 0 && (
        <Typography variant="body1" sx={{ mt: 1 }}>
          Read more at{' '}
          {sources.map((source, idx) => {
            const label = source.name || 'source';
            const href = source.url;
            return (
              <Box component="span" key={`${label}-${idx}`}>
                {idx > 0 && (idx === sources.length - 1 ? ' and ' : ', ')}
                {href ? (
                  <Link href={href} rel="noopener noreferrer" target="_blank">
                    {label}
                  </Link>
                ) : (
                  <Box component="span">{label}</Box>
                )}
              </Box>
            );
          })}
          .
        </Typography>
      )}
    </Box>
  );
}
